const OPERATOR_SUSPEND_REASON = 'FIXTURE_OPERATOR_SUSPENDED';

const fixtureId = (value) => {
  const id = value?.fixtureId ?? value?.fixture_id ?? value?.id;
  return id == null ? null : String(id);
};

const eventTypes = (patch = {}) => (
  Array.isArray(patch.eventTypes) ? patch.eventTypes : patch.event_types ?? []
).map((value) => String(value || '').toUpperCase());

const fixtureRows = (patch = {}) => {
  const rows = [];
  if (patch.fixture) rows.push(patch.fixture);
  if (Array.isArray(patch.fixtures)) rows.push(...patch.fixtures);
  else if (patch.fixtures && typeof patch.fixtures === 'object') rows.push(...Object.values(patch.fixtures));
  if (Array.isArray(patch.ops)) {
    patch.ops.forEach((op) => {
      if ((op?.op ?? op?.type) === 'upsert' && op.fixture) rows.push(op.fixture);
    });
  }
  const unique = new Map();
  rows.forEach((row) => {
    const id = fixtureId(row);
    if (id != null && !unique.has(id)) unique.set(id, row);
  });
  return [...unique.values()];
};

export const resolveOperatorVisibilityPatch = (patch = {}) => {
  const suspendedIds = new Set();
  if (
    patch?.visibility?.visible === false
    && patch?.visibility?.reason === OPERATOR_SUSPEND_REASON
  ) {
    const id = fixtureId(patch);
    if (id != null) suspendedIds.add(id);
  }
  if (Array.isArray(patch.ops)) {
    patch.ops.forEach((op) => {
      if (
        (op?.op ?? op?.type) === 'remove'
        && (op?.reason === OPERATOR_SUSPEND_REASON || patch?.reason === OPERATOR_SUSPEND_REASON)
      ) {
        const id = fixtureId(op);
        if (id != null) suspendedIds.add(id);
      }
    });
  }
  if (suspendedIds.size > 0) {
    return { action: 'suspend', fixtureIds: [...suspendedIds], fixtures: [] };
  }

  const rows = fixtureRows(patch);
  if (eventTypes(patch).includes('FIXTURE_VISIBILITY') && rows.length > 0) {
    return {
      action: 'resume',
      fixtureIds: rows.map(fixtureId).filter(Boolean),
      fixtures: rows,
    };
  }
  return { action: null, fixtureIds: [], fixtures: [] };
};

export const stripOperatorVisibilityRemovals = (patch = {}) => {
  if (!Array.isArray(patch.ops)) return patch;
  const ops = patch.ops.filter((op) => !(
    (op?.op ?? op?.type) === 'remove'
    && (op?.reason === OPERATOR_SUSPEND_REASON || patch?.reason === OPERATOR_SUSPEND_REASON)
  ));
  return ops.length === patch.ops.length ? patch : { ...patch, ops };
};

export const upsertOperatorResumedFixtures = (fixtures = [], visibilityPatch = {}, maxRows = null) => {
  if (visibilityPatch.action !== 'resume') return fixtures;
  const map = new Map(fixtures.map((row) => [fixtureId(row), row]));
  const limit = Number.isSafeInteger(Number(maxRows)) && Number(maxRows) > 0
    ? Number(maxRows)
    : null;
  visibilityPatch.fixtures.forEach((row) => {
    const id = fixtureId(row);
    if (id == null) return;
    map.set(id, map.has(id) ? { ...map.get(id), ...row } : row);
  });
  const rows = [...map.values()];
  if (limit == null || rows.length <= limit) return rows;
  const resumedIds = new Set(visibilityPatch.fixtureIds);
  const ordered = rows
    .map((row, index) => ({ row, index, order: Number(row?.rowOrder ?? row?.row_order) }))
    .sort((a, b) => {
      const aOrder = Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
      const bOrder = Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder || a.index - b.index;
    })
    .map(item => item.row);
  const resumedRows = ordered.filter((row) => resumedIds.has(fixtureId(row))).slice(0, limit);
  const selectedIds = new Set(resumedRows.map(fixtureId));
  ordered.forEach((row) => {
    if (selectedIds.size >= limit) return;
    const id = fixtureId(row);
    if (id != null) selectedIds.add(id);
  });
  return ordered.filter((row) => selectedIds.has(fixtureId(row))).slice(0, limit);
};

export const sportsnewOperatorVisibilityConstants = Object.freeze({ OPERATOR_SUSPEND_REASON });
